import {
  PROJECT_REFERENCES_INGEST_THREAD_TITLE,
  projectAwaitsStudy,
  shouldOpenProjectStudyDialog,
  type ProjectStudyState,
} from "@afar-research/afar";

/** The thread shell fields the empty thread reads from a project's other threads. */
export interface EmptyThreadSiblingThread {
  readonly id: string;
  readonly title: string;
  readonly latestTurn: { readonly state: string } | null;
}

/**
 * What an empty thread shows, following the project creation pipeline
 * (ADR 0141, re-keyed by ADR 0142): a project created from references first
 * ingests them, then asks what to study; every other project offers the verbs.
 */
export type EmptyThreadState =
  | {
      readonly type: "ingesting";
      /** The project's ingest thread, when this client has it. */
      readonly ingestThreadId: string | null;
      /** The ingest's latest turn stopped short of completing, so the user retries there. */
      readonly stalled: boolean;
    }
  | { readonly type: "study" }
  | { readonly type: "starters" };

export function deriveEmptyThreadState(input: {
  readonly project: ProjectStudyState | null;
  /** The project's threads, the empty one excluded. */
  readonly threads: ReadonlyArray<EmptyThreadSiblingThread>;
}): EmptyThreadState {
  const project = input.project;
  if (project === null || !projectAwaitsStudy(project)) return { type: "starters" };
  if (shouldOpenProjectStudyDialog(project, input.threads)) return { type: "study" };
  // While the project awaits its study it keeps to its ingest thread, so that
  // is the one thread it has; the title only breaks a tie.
  const ingestThread =
    input.threads.find((thread) => thread.title === PROJECT_REFERENCES_INGEST_THREAD_TITLE) ??
    input.threads[0] ??
    null;
  const latestState = ingestThread?.latestTurn?.state ?? null;
  return {
    type: "ingesting",
    ingestThreadId: ingestThread?.id ?? null,
    stalled: latestState === "error" || latestState === "interrupted",
  };
}

/**
 * The one quiet line a thread that ran in a retired Feature Workspace shows:
 * its history is kept and new turns run at the project root.
 */
export function legacyWorkingDirectoryNotice(
  workingDirectory: string | null | undefined,
): string | null {
  if (workingDirectory == null || workingDirectory.trim().length === 0) return null;
  const segment = workingDirectory.replace(/\/+$/u, "").split("/").at(-1) ?? "";
  const workspace =
    segment.length === 0 ? "" : `${segment.charAt(0).toUpperCase()}${segment.slice(1)} `;
  return `This thread ran in the old ${workspace}workspace (${workingDirectory}). Its history is kept; new turns run at the project root.`;
}
